"use client";

import { useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useSession } from "next-auth/react";
import useApi from "@/hooks/useApi";
import {
  disableVisitorTracking,
  getVisitorId,
  isMainSiteHost,
  isVisitorTrackingDisabled,
  setVisitorTrackingActive,
  startVisitorTracker,
  trackVisitorEvent,
} from "@/lib/visitorTracker";

// Pages of businesses' own clients (booking sites, client hub, forms).
const EXCLUDED_PREFIXES = [
  "/tenant",
  "/client-hub",
  "/login/client",
  "/public-form",
  "/forms",
  "/a/",
  "/api",
];

// Inside the business app only the steps that turn a sign-up into an active
// account are part of the journey.
const SIGNED_IN_PREFIXES = ["/onboarding", "/create-business"];

const startsWithAny = (path, prefixes) =>
  prefixes.some((prefix) => path === prefix.replace(/\/$/, "") || path.startsWith(prefix));

/**
 * Feeds SA → CRM → Visitor Analytics: page views and clicks on the public
 * site (and onboarding), and links the browser to the account once signed in.
 */
export default function VisitorTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { data: session, status } = useSession();
  const api = useApi();
  const lastTracked = useRef(null);
  const search = searchParams?.toString() || "";

  const role = session?.role;
  const isSuperAdmin = role === "super_admin";

  // A super admin's browser is never counted, even after they sign out.
  useEffect(() => {
    if (isSuperAdmin) disableVisitorTracking();
  }, [isSuperAdmin]);

  const shouldTrack =
    status !== "loading" &&
    !isSuperAdmin &&
    role !== "client" &&
    !startsWithAny(pathname || "", EXCLUDED_PREFIXES) &&
    (status !== "authenticated" || startsWithAny(pathname || "", SIGNED_IN_PREFIXES));

  useEffect(() => {
    if (status === "loading") return;
    if (!isMainSiteHost() || isVisitorTrackingDisabled()) {
      setVisitorTrackingActive(false);
      return;
    }
    startVisitorTracker();
    setVisitorTrackingActive(shouldTrack);
    if (!shouldTrack) return;

    const key = `${pathname}?${search}`;
    if (lastTracked.current === key) return;
    lastTracked.current = key;

    // Let the new page set its <title> first.
    const timer = setTimeout(() => {
      trackVisitorEvent("page_view", {
        path: pathname,
        label: document.title,
        meta: search ? { query: search.slice(0, 200) } : undefined,
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [pathname, search, shouldTrack, status]);

  // Tie this browser to the signed-in business user (covers Google sign-up,
  // which never sends the visitor id header). Once per user per browser.
  useEffect(() => {
    if (status !== "authenticated" || !session?.id || isSuperAdmin || role === "client") return;
    if (!isMainSiteHost() || isVisitorTrackingDisabled()) return;
    const visitorId = getVisitorId();
    if (!visitorId) return;
    const flag = `zb_vid_user_${session.id}`;
    try {
      if (window.localStorage.getItem(flag)) return;
    } catch {
      return;
    }
    // Remember only once the API confirms the link, so a failed or early
    // call is retried on the next page load.
    api.post("/api/site-events/identify", { visitor_id: visitorId }, false).then(({ data }) => {
      if (!data?.data?.linked) return;
      try {
        window.localStorage.setItem(flag, "1");
      } catch {
        // Storage blocked: we'll just link again next time (harmless).
      }
    });
  }, [api, isSuperAdmin, role, session?.id, status]);

  return null;
}
